// src/shared/dashboard/shell/MentorPanel.tsx
//
// The AI-coach entry point in the right rail: a card that introduces the coach
// and hands off to /supervisor with one CTA. It used to carry an inline
// Composer that stashed the typed question in sessionStorage — a second, lesser
// chat sitting beside the real one. The rail says who the coach is and gets you
// there; the conversation itself belongs on the coach page.
//
// The card is a plain container with ONE interactive element inside it, not a
// giant clickable div wrapping a button — nesting those breaks keyboard and
// screen-reader semantics.
//
// Labelled "AI Coach" and "Talk to our AI agent" so it is unmistakable that the
// counterpart is a model, not a person waiting on the other end. The persona
// name still comes from the vertical's supervisor identity, so the button reads
// Dr. Rachel Emery in general and the therapy/crisis supervisor elsewhere,
// shortened to "Dr. Emery" for this card only — the config and the system
// prompt keep her full name. The
// right-rail column (gray frame + sticky inset) and any sibling cards above it
// are owned by AppShell; this is just the button. Only mounted when the vertical
// enables `supervisor`.
import { useT } from "@/shared/i18n/useT";
import { useVertical } from "@/shared/contexts/VerticalContext";
import { go } from "@/shared/dashboard/shell/navigate";

export function MentorPanel() {
  const t = useT();
  const { verticalConfig } = useVertical();

  const mentorName = verticalConfig.supervisor?.identity?.name ?? "Mentor";

  // Display-only shortening for the narrow rail: a titled three-part name
  // drops the middle, so "Dr. Rachel Emery" reads "Dr. Emery". Names without a
  // title, or with only two parts, are left exactly as configured.
  const parts = mentorName.split(" ").filter(Boolean);
  const shortName =
    parts.length > 2 && parts[0].endsWith(".")
      ? `${parts[0]} ${parts[parts.length - 1]}`
      : mentorName;

  const initials = shortName
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="w-full border border-(--hairline) bg-white p-5 shadow-(--shadow-surface)">
      <p className="ed-mono text-[10px] text-[var(--muted)]">
        {t("shell.aiCoach")}
      </p>

      <div className="mt-3.5 flex items-center gap-3">
        <span
          aria-hidden="true"
          className="grid h-12 w-12 shrink-0 place-items-center bg-[var(--ink)] text-[var(--paper)] ed-mono text-[14px]"
        >
          {initials || "AI"}
        </span>
        <h3 className="ed-serif min-w-0 truncate text-[19px] text-[var(--ink-2)]">
          {shortName}
        </h3>
      </div>

      <div
        className="mt-4 border-l-2 px-3.5 py-3"
        style={{
          borderColor: "var(--accent)",
          background: "color-mix(in srgb, var(--accent) 5%, #fff)",
        }}
      >
        <p className="text-[13px] leading-relaxed text-[var(--fg)]">
          {t("shell.aiCoachBlurb")}
        </p>
      </div>

      <button
        type="button"
        onClick={() => go("/supervisor")}
        className="mt-4 flex min-h-11 w-full cursor-pointer items-center justify-center bg-[var(--ink)] px-4 text-[13px] font-semibold text-[var(--paper)] transition-colors hover:bg-[var(--ink-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:ring-offset-2"
      >
        {t("shell.aiCoachCta")}
      </button>
    </div>
  );
}
